/**
 * Injects badges.css into shadow DOM roots for authorized.by badges
 */
(function() {
  'use strict';
  
  // Cache for the CSS content
  /** @type {string | null} */
  let badgesCSS = null;
  /** @type {WeakSet<ShadowRoot>} */
  const processedShadowRoots = new WeakSet();
  
  /**
   * Fetches the CSS content from badges.css
   * @returns {Promise<string | null>}
   */
  async function fetchBadgesCSS() {
    if (badgesCSS) return badgesCSS;
    
    // Get the CSS URL from the script tag's data attribute
    const scriptTag = document.querySelector('script[data-badges-css]');
    const cssUrl = scriptTag?.getAttribute('data-badges-css');
    
    if (!cssUrl) {
      console.warn('Badges CSS URL not found');
      return null;
    }
    
    try {
      const response = await fetch(cssUrl);
      if (response.ok) {
        badgesCSS = await response.text();
        return badgesCSS;
      }
    } catch (error) {
      console.warn('Failed to load badges.css:', error);
    }
    return null;
  }
  
  /**
   * Injects CSS into a shadow root
   * @param {ShadowRoot} shadowRoot
   * @param {string} cssText
   */
  function injectCSSIntoShadowRoot(shadowRoot, cssText) {
    if (!cssText || processedShadowRoots.has(shadowRoot)) return;
    
    const style = document.createElement('style');
    style.textContent = cssText;
    shadowRoot.insertBefore(style, shadowRoot.firstChild);
    processedShadowRoots.add(shadowRoot);
  }
  
  /**
   * Finds all shadow roots in the document and injects CSS
   * @param {string} cssText
   */
  function injectIntoAllShadowRoots(cssText) {
    // Find all elements that might have shadow roots
    const allElements = document.querySelectorAll('*');
    
    allElements.forEach(element => {
      if (element.shadowRoot) {
        injectCSSIntoShadowRoot(element.shadowRoot, cssText);
        
        // Also check nested shadow roots
        const shadowElements = element.shadowRoot.querySelectorAll('*');
        shadowElements.forEach(shadowElement => {
          if (shadowElement.shadowRoot) {
            injectCSSIntoShadowRoot(shadowElement.shadowRoot, cssText);
          }
        });
      }
    });
  }
  
  /**
   * Sets up a MutationObserver to watch for new shadow DOM elements
   * @param {string} cssText
   */
  function setupShadowDOMObserver(cssText) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            /** @type {Element} */
            const element = /** @type {Element} */ (node);
            
            // Check if the node itself has a shadow root
            if ('shadowRoot' in element && element.shadowRoot) {
              injectCSSIntoShadowRoot(element.shadowRoot, cssText);
            }
            
            // Check all descendants for shadow roots
            const shadowElements = element.querySelectorAll ? element.querySelectorAll('*') : [];
            shadowElements.forEach(/** @type {Element} */ (shadowElement) => {
              if ('shadowRoot' in shadowElement && shadowElement.shadowRoot) {
                injectCSSIntoShadowRoot(shadowElement.shadowRoot, cssText);
              }
            });
          }
        });
      });
      
      // Also check any connected elements
      injectIntoAllShadowRoots(cssText);
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    return observer;
  }
  
  /**
   * Initialize the CSS injection
   */
  async function init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }
    
    // Fetch CSS
    const cssText = await fetchBadgesCSS();
    if (!cssText) return;
    
    // Inject into existing shadow roots
    injectIntoAllShadowRoots(cssText);
    
    // Set up observer for future shadow roots
    setupShadowDOMObserver(cssText);
    
    // Also try after a short delay to catch late-loaded elements
    setTimeout(() => {
      injectIntoAllShadowRoots(cssText);
    }, 1000);
    
    // And after the authorized.by script might have loaded
    setTimeout(() => {
      injectIntoAllShadowRoots(cssText);
    }, 3000);
  }
  
  // Start initialization
  init();
})();
